import { PeopleTwoTone } from '@mui/icons-material';
import { Alert, Typography } from '@mui/material';
import React, { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGrid, LabelValue, useSelectedOrganisation, Widget } from '../../../shared';
import { IInfoSupportee, ISupportCaseInfo } from '../../models';
import { Stack } from '@mui/system';

interface Props {
    supportCase: ISupportCaseInfo;
    hasFoodRestrictions?: boolean;
    footer?: ReactNode;
    isUnconfirmed?: boolean;
}

export const InfoSupportees: FC<Props> = ({
    supportCase,
    hasFoodRestrictions = false,
    footer,
    isUnconfirmed = false,
}) => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();
    const adults = supportCase.supportees
        .filter((supportee) => supportee.age >= 18)
        ?.sort((a) => (a.isPrimary ? -1 : 1));
    const children = supportCase.supportees.filter((supportee) => supportee.age < 18 && supportee.age != null);
    const unknown = supportCase.supportees.filter((supportee) => supportee.age === null);

    const formatDependencyPercentage = (supportee: IInfoSupportee) => {
        return supportee.dependencyPercentage === 100 ? '' : ` (${supportee.dependencyPercentage}% ${t('dependency')})`;
    };
    const formatSupportee = (supportee: IInfoSupportee) => {
        if (supportee.age === null)
            return (
                (supportee.firstName ? `${supportee.firstName} ${supportee.lastName}` : `${t('anonymousLabel')}`) +
                formatDependencyPercentage(supportee)
            );
        return supportee.firstName
            ? `${supportee.firstName} ${supportee.lastName} (${supportee.age} ${t('yearOld')}${supportee.dependencyPercentage === 100 ? ')' : `, ${supportee.dependencyPercentage}% ${t('dependency')})`}`
            : `${supportee.age} ${t('yearOld')}` + `${formatDependencyPercentage(supportee)}`;
    };

    const organisationHasAccess = useMemo(
        () =>
            window.carity.environment.shareSupportCases ||
            supportCase?.isUnconfirmed ||
            supportCase?.organisations.find((org) => org.id === organisation?.id),
        [supportCase, organisation],
    );

    console.log('isUnconfirmed', isUnconfirmed);

    const showSupportees = (supportees: IInfoSupportee[]) => {
        if (supportees?.length === 0) return '/';
        return (
            <Stack direction="row" flexWrap="wrap">
                {supportees.map((supportee, i) => (
                    <Typography
                        sx={{
                            fontWeight: supportee.isPrimary ? 'bold' : 'normal',
                            opacity: 0.7,
                            span: { pr: '4px' },
                            mb: 0,
                        }}
                        variant="caption"
                        key={supportee.id}
                    >
                        {formatSupportee(supportee)}
                        {supportees[i + 1] !== undefined && <span>{','}</span>}
                    </Typography>
                ))}
            </Stack>
        );
    };

    return (
        <Widget
            icon={<PeopleTwoTone color="primary" />}
            title={`${t('supportees')} (${supportCase.supportees.length})`}
            footer={footer}
            sx={{ maxWidth: '100vw' }}
        >
            <FormGrid
                containerProps={{ spacing: '4px', direction: 'row' }}
                size={hasFoodRestrictions || isUnconfirmed ? { xs: 12 } : { xs: 12, lg: 6 }}
            >
                <Stack spacing={1} data-testid="supportees">
                    {!organisationHasAccess && <Alert severity="warning">{t('unknownSupportCaseWarning')}</Alert>}
                    <LabelValue label={t('adults')} value={showSupportees(adults)} />
                    <LabelValue label={t('children')} value={showSupportees(children)} />
                    <LabelValue label={t('ageUnknown')} value={showSupportees(unknown)} />
                </Stack>
                <Stack spacing={1}>
                    <LabelValue
                        label={t('familySituation')}
                        value={
                            <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                {supportCase.familySituation
                                    ? t(supportCase.familySituation)
                                    : t('emptyStateFamilySituation')}
                            </Typography>
                        }
                    />
                    <LabelValue
                        label={t('livingSituation')}
                        value={
                            <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                {supportCase.livingSituation
                                    ? t(supportCase.livingSituation)
                                    : t('emptyStateLivingSituation')}
                            </Typography>
                        }
                    />
                </Stack>
            </FormGrid>
        </Widget>
    );
};
